import { useTranslation } from "react-i18next";
import {
  fetchExcelProducts,
  fetchExcelTemplate,
  postExcelTemplate,
} from "../api/excel.api";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";

const useExcel = () => {
  const { t } = useTranslation();
  const [bearerToken, toast, companyId, organizationId] = useUIStore(
    useShallow((state: UIState) => [
      state.bearerToken,
      state.toastRef,
      state.companyId,
      state.organizationId,
    ]),
  );

  const downloadExcelTemplate = async () => {
    try {
      const data = await fetchExcelTemplate(bearerToken);
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (error) {
      console.error("Error downloading Excel template:", error);
    }
  };

  const exportExcel = async () => {
    try {
      const data = await fetchExcelProducts(
        bearerToken as string,
        companyId as number,
        organizationId ? organizationId : undefined,
      );
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "exported_products.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting Excel file:", error);
      throw error;
    }
  };

  const postExcel = async (
    file: File,
    existingReportId?: number,
    year?: number,
    quarter?: number,
    month?: number,
  ) => {
    try {
      const response = await postExcelTemplate(
        file,
        bearerToken as string,
        companyId as number,
        organizationId ? organizationId : undefined,
        existingReportId,
        year,
        quarter,
        month,
      );

      if (response && response.responseType === "excel") {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "ecomodtemplate.xlsx");
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);

        toast?.current?.show({
          severity: "success",
          detail: t("excel-phase-1-success"),
          life: 5000,
        });

        // The prefilled excel returned here can't carry a JSON body, so any
        // warnings/errors are reported via headers instead; surface them
        // through the same summary dialog as a completed import.
        if (response.warnings?.length || response.errors?.length) {
          return { warnings: response.warnings, errors: response.errors };
        }
      }

      // If the response is 201, it means a complete excel file was uploaded
      // and we can create report with the data. Caller shows the summary
      // dialog and handles navigation once the user acknowledges it.
      if (response && response.responseType === "json") {
        return response.data;
      }
    } catch (error) {
      console.error("Error uploading Excel file:", error);
      toast?.current?.show({
        severity: "error",
        summary: t("excel-upload-error"),
        detail: error instanceof Error ? error.message : undefined,
        life: 8000,
      });
      throw error;
    }
  };
  return { downloadExcelTemplate, postExcel, exportExcel };
};

export default useExcel;
